import { useEffect, useId, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ExternalLink } from 'lucide-react';

import InfoList from './InfoList';
import { userAPI } from '../UserAPI';
import { Building, EntityQuery, Query, Unit } from '@the-real-insight/tri-model';
import { useAdministrationAPI } from '../AdministrationAPI';
import { useModalContext } from '../components/ModalProvider';
import EntityPhotoListEditor, { EntityPhoto } from '../components/EntityPhotoListEditor';
import Section from '../components/Section';

import { useCompaniesAtBuilding } from './useCompaniesAtBuilding';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';

class Properties {
    building: any;
    onBuildingPatch?: (patch: Record<string, unknown>) => void;
}

const numberFormat0Digits = new Intl.NumberFormat('de', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const numberFormat2Digits = new Intl.NumberFormat('de', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function GeneralBuildingProperties(properties: Properties) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { updateEntity } = useAdministrationAPI();
    const { pushError } = useModalContext() as any;
    const usageClassId = useId();

    const persistPhotos = async (photos: EntityPhoto[]) => {
        const b = properties.building;
        if (!b?._id) {
            return;
        }
        try {
            await updateEntity(new Building({ ...b, photos }));
            properties.onBuildingPatch?.({ photos });
        } catch (e) {
            pushError(e);
        }
    };
    const persistUsageClass = async (usageClass: string) => {
        const b = properties.building;
        if (!b?._id) {
            return;
        }
        try {
            await updateEntity(new Building({ ...b, usageClass }));
            properties.onBuildingPatch?.({ usageClass });
        } catch (e) {
            pushError(e);
        }
    };
    // Building-use vocabulary comes from the server (Legal.UsageClass) — a copy
    // in the portal would drift the moment the vocabulary is extended.
    const [usageClasses, setUsageClasses] = useState<any[]>([]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const list = await userAPI.getUsageClasses();
                if (!cancelled) setUsageClasses(list);
            } catch {
                if (!cancelled) setUsageClasses([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const [buildingHeight, setBuildingHeight] = useState() as any;
    const [_units, setUnits] = useState([] as any);

    useEffect(() => {
        const call = async () => {
            const result = await userAPI.query(new Query(new EntityQuery(Unit.type)));

            setUnits(result.map((unit: any, index: number) => {
                return {
                    key: index,
                    name: unit.name,
                    tenant: 'Bla',
                };
            }));
        };

        call();
    }, []);

    // Companies registered at this building (indexed server route, once per building/address).
    const { rows: subsidiaries } = useCompaniesAtBuilding(properties.building);

    const centreLat = Number.isFinite(properties.building?.minLat) && Number.isFinite(properties.building?.maxLat)
        ? 0.5 * (properties.building.minLat + properties.building.maxLat) : undefined;
    const centreLon = Number.isFinite(properties.building?.minLon) && Number.isFinite(properties.building?.maxLon)
        ? 0.5 * (properties.building.minLon + properties.building.maxLon) : undefined;

    useEffect(() => {
        if (centreLat === undefined || centreLon === undefined) {
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const cityObject = await userAPI.getCityObject(centreLat, centreLon);

                if (!cancelled && cityObject) {
                    setBuildingHeight(cityObject.measuredHeight);
                }
            } catch (e) {
                console.error('Could not load city object', e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [centreLat, centreLon]);

    return <div className="flex min-w-0 max-w-[1400px] flex-col gap-4">
        {/* Context part: how many and how vulnerable are the people affected.
            Derivable from building law, so it is a closed selection and never
            free text. A missing value is a data gap — it is never replaced by
            a default, it is pointed out. */}
        <Section title={t('buildingProperties.generalBuildingPropertiesPanel.usageSection')}>
            <div className="flex max-w-2xl flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                    <Label htmlFor={usageClassId}>{t('context.usageClass')}</Label>
                    <Select
                        value={properties.building?.usageClass ?? undefined}
                        onValueChange={(value: string) => {
                            if (!value) return;
                            void persistUsageClass(value);
                        }}
                    >
                        {/* max-sm:h-11 hebt das Touch-Ziel auf die vom
                            Layout-Kontrakt geforderten 44px. */}
                        <SelectTrigger id={usageClassId} className="max-sm:h-11">
                            <SelectValue placeholder={t('context.notRecorded')} />
                        </SelectTrigger>
                        {/* Die längsten Einträge ("Tageseinrichtung
                            (Kinder/Behinderte/alte Menschen)") sollen umbrechen
                            statt abzuschneiden; auf schmalen Viewports bleibt
                            die Liste innerhalb des Bildschirms. */}
                        <SelectContent className="max-w-[calc(100vw-2rem)]">
                            {usageClasses.filter((entry: any) => entry?.usageClass).map((entry: any) => (
                                <SelectItem
                                    key={entry.usageClass}
                                    value={entry.usageClass}
                                    className="whitespace-normal max-sm:py-3"
                                >
                                    {entry.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                        {t('context.usageClassHelp')}
                    </p>
                </div>
                {!properties.building?.usageClass ? (
                    <Alert variant="warning">
                        <AlertTriangle aria-hidden="true" className="h-4 w-4" />
                        <AlertDescription>{t('context.usageClassMissing')}</AlertDescription>
                    </Alert>
                ) : null}
            </div>
        </Section>

        <Section title={t('buildingProperties.generalBuildingPropertiesPanel.keyFigures')}>
            <InfoList list={[
                {
                    label: t('buildingProperties.generalBuildingPropertiesPanel.yearOfConstruction'),
                    value: '-',
                },
                {
                    label: 'Gebäudehöhe',
                    value: buildingHeight ? numberFormat2Digits.format(buildingHeight) : '-',
                    suffix: 'm'
                },
                {
                    label: t('buildingProperties.generalBuildingPropertiesPanel.levels'),
                    value: properties.building.levels || (buildingHeight !== undefined ? `${Math.round(buildingHeight / 3.2)} (geschätzt)` : '-'),
                },
                {
                    label: t('buildingProperties.generalBuildingPropertiesPanel.grossFloorArea'),
                    value: properties.building.area ? `${numberFormat0Digits.format(properties.building.area)} ㎡` : '-',
                },
                {
                    label: t('buildingProperties.generalBuildingPropertiesPanel.numberOfUnits'),
                    value: '-',
                },
            ]}></InfoList>
        </Section>

        {/* Der Fotobereich bringt Überschrift, Raster und Dialog in
            EntityPhotoListEditor mit — hier steht nur die Karte darum, sonst
            stünde "Fotos" zweimal übereinander. */}
        <div className="min-w-0 rounded-xl border bg-card p-4 text-card-foreground shadow-sm">
            <EntityPhotoListEditor
                photos={properties.building?.photos}
                onPhotosChange={persistPhotos}
                disabled={!properties.building?._id}
            />
        </div>

        {subsidiaries && subsidiaries.length > 0
            ?
            <Section title={t('buildingProperties.generalBuildingPropertiesPanel.subsidiaries')}>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t('buildingProperties.generalBuildingPropertiesPanel.company')}</TableHead>
                            <TableHead>{t('buildingProperties.generalBuildingPropertiesPanel.contact')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {subsidiaries.map((subsidiary: any, index: number) => (
                            <TableRow key={`${subsidiary[13] ?? subsidiary[0] ?? ''}-${index}`}>
                                <TableCell className="align-top">
                                    <div className="flex min-w-0 items-center gap-1">
                                        <span className="min-w-0 break-words font-bold">{subsidiary[0]}</span>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 shrink-0 max-sm:h-11 max-sm:w-11"
                                            aria-label={t('buildingProperties.generalBuildingPropertiesPanel.openCompany')}
                                            onClick={() => navigate(`/entity?selectedEntityType=LegalPerson&selectedEntity=${subsidiary[13]}`)}
                                        >
                                            <ExternalLink aria-hidden="true" className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    <div className="mt-1 break-words text-xs text-muted-foreground">
                                        {subsidiary[3]}, {subsidiary[1]}
                                    </div>
                                </TableCell>
                                <TableCell className="align-top">
                                    <div className="break-words">{subsidiary[10]}</div>
                                    <div className="break-words">
                                        <a className="underline underline-offset-2 hover:no-underline" href={`emailto:${subsidiary[11]}`}>{subsidiary[11]}</a>
                                    </div>
                                    <div className="break-words">
                                        <a className="underline underline-offset-2 hover:no-underline" href={subsidiary[12]}>{subsidiary[12]}</a>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Section>
            :
            <></>
        }
    </div>;
}
